import React, { useMemo } from 'react';
import { capitalize, toLower } from 'lodash';
import {
	CourseEntity, CourseOfferingEntity, PlanSelectionEntity, RequirementEntity,
} from 'Models/Entities';
import { DataIndex } from 'Util/DataIndexUtils';
import {
	buildRequirementTree,
	checkRequirementRulesViolation,
	RequirementNode,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import PdfCourseAccordionItem from 'Views/Pages/Pdf/Components/PDFCourseAccordionItem';
import {
	DuplicateCourseItem,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/DuplicateCourseSelection';
import { RequisiteWarning } from 'Queries/Hooks/StudyPlan/types';
import { compareTwoDate } from 'Util/DateTimeUtils';
import PdfCourseAccordion from 'Views/Pages/Pdf/Components/PDFCourseAccordion';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import PdfCourseAccordionLabel from 'Views/Pages/Pdf/Components/PdfCourseAccordionLabel';
import PdfCourseSelectionNotice from 'Views/Pages/Pdf/Components/PDFCourseSelectionNotice';
import PdfChipGroup from 'Views/Pages/Pdf/Chip/PDFChipGroup';
import PdfChip from 'Views/Pages/Pdf/Chip/PdfChip';
import PdfWarningBanner from 'Views/Pages/Pdf/Components/PDFWarningBanner';
import { View, Text } from '@react-pdf/renderer';
import PdfSection from 'Views/Pages/Pdf/Components/PDFSection';

interface ProgramComponentsProps {
	requirementsById: DataIndex<RequirementEntity>,
	coursesById: DataIndex<CourseEntity>,
	planSelections: PlanSelectionEntity[],
	courseOfferingsById: DataIndex<CourseOfferingEntity>,
	requirementsByParentId: DataIndex<RequirementEntity[]>,
	duplicateCourses: DuplicateCourseItem[],
	completedCourseIds: string[],
	requisiteWarningsByCourseId: DataIndex<RequisiteWarning[]>,
}

const ProgramComponents = (props: ProgramComponentsProps) => {
	const {
		requirementsById,
		coursesById,
		planSelections,
		courseOfferingsById,
		requirementsByParentId,
		duplicateCourses,
		completedCourseIds,
		requisiteWarningsByCourseId,
	} = props;

	// Get Root requirement IDs from requirement index
	const rootRequirementIds: string[] = useMemo(() => {
		if (!requirementsById) {
			return [] as string[];
		}

		return Object.values(requirementsById)
			.filter(requirement => !requirement.parentRequirementId)
			.map(requirement => requirement.id!);
	}, [requirementsById]);

	// Get root requirements
	const components = useMemo(() => {
		return rootRequirementIds.map(id => requirementsById[id])
			.sort((a, b) => a.order - b.order)
			.map(requirement => new RequirementEntity(requirement));
	}, [requirementsById, rootRequirementIds]);

	/**
	 * Index course offering IDs by course IDs then by requirement IDs
	 * {
	 *     requirementId: {
	 *         courseId: ["course-offering-id"]
	 *     }
	 * }
	 */
	const selectedOfferingIdsByCourseIdByRequirementId = useMemo(() => {
		const _offeringsByCourseIdByRequirementId: DataIndex<DataIndex<string[]>> = {};

		for (const { courseOfferingId, locationId, courseId } of planSelections) {
			const existingCourses = _offeringsByCourseIdByRequirementId[locationId];
			if (!existingCourses) {
				_offeringsByCourseIdByRequirementId[locationId] = {};
			}

			const existingCourseOfferings = _offeringsByCourseIdByRequirementId[locationId][courseId];
			if (!existingCourseOfferings) {
				_offeringsByCourseIdByRequirementId[locationId][courseId] = [];
			}

			if (courseOfferingId) {
				_offeringsByCourseIdByRequirementId[locationId][courseId].push(courseOfferingId);
			}
		}

		return _offeringsByCourseIdByRequirementId;
	}, [planSelections]);

	/**
	 * Build the requirement tree of requirement
	 */
	const requirementTree = useMemo(() => {
		if (!components.length || !planSelections) {
			return {};
		}

		return buildRequirementTree(
			selectedOfferingIdsByCourseIdByRequirementId,
			components,
			requirementsByParentId,
			planSelections,
			coursesById,
			courseOfferingsById,
		);
	}, [
		coursesById,
		courseOfferingsById,
		components,
		selectedOfferingIdsByCourseIdByRequirementId,
		requirementsByParentId,
		planSelections,
	]);

	const renderRequirementTree = (requirementTreeNode: RequirementNode, depth: number) => {
		const { requirement } = requirementTreeNode;

		if (requirement.category === 'REQUIREMENTS') {
			return renderRequirementAccordion(requirementTreeNode, depth);
		}

		return renderCourseAccordion(requirementTreeNode, depth);
	};

	const renderRequirementAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		const childNodes = requirementNode.childNodes
			.sort(
				(a, b) => compareTwoDate(
					new Date(a.requirement.created),
					new Date(b.requirement.created),
				),
			);

		return (
			<PdfCourseAccordion<RequirementNode>
				key={requirement.id}
				title={requirement.displayName}
				depth={depth}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={childNodes}
				renderItem={(item, index) => {
					return renderRequirementTree(item, depth + 1);
				}}
				emptyLabel={`No ${window.lexicon.courses}`}
				description={(
					<Conditional
						condition={requirement.description}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<Text style={{ fontStyle: 'italic' }}>{requirement.description}</Text>
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
				errorMessage={(
					<Conditional
						condition={hasViolatedRequirementRules}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<PdfCourseSelectionNotice />
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
			/>
		);
	};

	const renderCourseAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const courses = Object.keys(requirementNode.offeringIdsByCourseId)
			.map(courseId => coursesById[courseId])
			.sort((a, b) => a.code.localeCompare(b.code));

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		return (
			<PdfCourseAccordion<CourseEntity>
				key={requirement.id}
				title={requirement.displayName}
				depth={depth}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={courses}
				renderItem={(course, index) => {
					const courseWarning = requisiteWarningsByCourseId[course.id];

					return (
						<PdfCourseAccordionItem
							key={course.id}
							course={course}
							label={
								duplicateCourses.some(c => c.course.id === course.id)
									? (
										<PdfChipGroup>
											<PdfChip label="Duplicate" />
										</PdfChipGroup>
									)
									: null
							}
							additionalStatus={
								completedCourseIds.includes(course.id)
									? (
										<PdfChipGroup>
											<PdfChip label={'Completed/Credit'} isSuccess />
										</PdfChipGroup>
									)
									: null
							}
							warning={courseWarning?.map(warning => (
								<PdfWarningBanner
									title={warning.title}
									description={warning.description}
								/>
							))}
						/>
					);
				}}
				emptyLabel="No courses"
				description={(
					<Conditional
						condition={requirement.description}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<Text style={{ fontStyle: 'italic' }}>{requirement.description}</Text>
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
				errorMessage={(
					<Conditional
						condition={hasViolatedRequirementRules}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<PdfCourseSelectionNotice />
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
			/>
		);
	};

	return (
		<PdfSection
			title={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.components)}`}
			showDivider={false}
		>
			<View style={{ display: 'flex', gap: 8 }}>
				{components.map(component => {
					return renderRequirementTree(requirementTree[component.id], 0);
				})}
			</View>
		</PdfSection>
	);
};

export default ProgramComponents;
